// Страница ролика (DESIGN §6): крупный плеер, правки по таймкоду прямо на шкале, версии рядом
// с «что поменялось», автопроверка платформы, паспорт и тексты объявления. Главное действие —
// отобрать ролик или отправить правки агенту; у отобранного и живого — «Сделать варианты» (выгорающему
// карточка «Ролик выгорает» объясняет, что заметила платформа).
import { Button, Chip, EmptyState, IconButton, Skeleton, toast, usd, when } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CircleCheck,
  CircleX,
  Clapperboard,
  Copy,
  Download,
  MessageSquarePlus,
  Send,
  Shuffle,
  Trash2,
} from 'lucide-react';
import { type FormEvent, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import {
  type CommentView,
  type CreativeDetail,
  DELIVERY,
  mediaUrl,
  QC,
  statusOf,
  tc,
  useCreative,
  type VersionView,
  voiceGender,
} from './data';
import { VariantsPanel } from './Variants';
import './creatives.css';

const COMMENT_ST: Record<
  CommentView['status'],
  { label: string; tone: 'info' | 'accent' | 'success' | 'default' }
> = {
  open: { label: 'Новая', tone: 'accent' },
  in_work: { label: 'У агента', tone: 'info' },
  done: { label: 'Сделано', tone: 'success' },
  wontfix: { label: 'Не делаем', tone: 'default' },
};

function copy(text: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast('Скопировано'),
    () => toast('Не удалось скопировать'),
  );
}

/** Шкала под плеером: кадры монтажного листа, метки правок, текущее время. Клик — перемотка. */
function Timeline({
  v,
  time,
  comments,
  onSeek,
  active,
}: {
  v: VersionView;
  time: number;
  comments: CommentView[];
  onSeek: (t: number) => void;
  active: number | null;
}) {
  const dur = v.durationSec || v.clips.reduce((s, c) => s + c.dur, 0) || 1;
  const bar = useRef<HTMLDivElement>(null);
  const seekAt = (clientX: number) => {
    const r = bar.current?.getBoundingClientRect();
    if (!r) return;
    onSeek(Math.max(0, Math.min(dur, ((clientX - r.left) / r.width) * dur)));
  };
  let acc = 0;
  return (
    <div className="tline">
      <div
        ref={bar}
        className="tline-bar"
        role="slider"
        tabIndex={0}
        aria-label="Шкала ролика"
        aria-valuemin={0}
        aria-valuemax={Math.round(dur)}
        aria-valuenow={Math.round(time)}
        aria-valuetext={tc(time)}
        onPointerDown={(e) => seekAt(e.clientX)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') onSeek(Math.min(dur, time + 1));
          if (e.key === 'ArrowLeft') onSeek(Math.max(0, time - 1));
        }}
      >
        {v.clips.length > 0 &&
          v.clips.map((c, i) => {
            const left = (acc / dur) * 100;
            acc += c.dur;
            return (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: кадры монтажного листа по порядку
                key={i}
                className="tline-shot"
                style={{ left: `${left}%`, width: `${(c.dur / dur) * 100}%` }}
                title={`Кадр ${i + 1}: ${c.src ?? ''}`}
              />
            );
          })}
        <span className="tline-head" style={{ left: `${(time / dur) * 100}%` }} />
        {comments.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`tline-mark ${c.status}${active === c.id ? ' act' : ''}`}
            style={{ left: `${(c.tStart / dur) * 100}%` }}
            title={`${tc(c.tStart)} — ${c.text}`}
            aria-label={`Правка на ${tc(c.tStart)}: ${c.text}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onSeek(c.tStart)}
          />
        ))}
      </div>
      <div className="tline-times">
        <span>{tc(time)}</span>
        <span>{tc(dur)}</span>
      </div>
    </div>
  );
}

function QcList({ v }: { v: VersionView }) {
  const qc = QC[v.qcStatus] ?? { label: v.qcStatus, tone: 'default' as const };
  // Пока проверка идёт, в qc только отметка о попытке — показываем статус
  if (!v.qc || v.qcStatus === 'pending' || v.qcStatus === 'running')
    return (
      <p className="muted">
        <Chip tone={qc.tone} dot>
          {qc.label}
        </Chip>
      </p>
    );
  const order = { fail: 0, warn: 1, ok: 2, skip: 3 } as const;
  return (
    <ul className="qc-list">
      {[...v.qc.checks]
        .sort((a, b) => order[a.status] - order[b.status])
        .map((c) => (
          <li key={c.id} className={`qc-${c.status}`}>
            {c.status === 'ok' ? (
              <CircleCheck aria-hidden />
            ) : c.status === 'fail' ? (
              <CircleX aria-hidden />
            ) : c.status === 'warn' ? (
              <AlertTriangle aria-hidden />
            ) : (
              <span className="qc-skip" aria-hidden />
            )}
            <div>
              <b>{c.label}</b>
              <span>{c.detail}</span>
            </div>
          </li>
        ))}
    </ul>
  );
}

function Spec({ d }: { d: CreativeDetail }) {
  const s = (d.spec ?? {}) as Record<string, unknown>;
  const rows: Array<[string, unknown]> = [
    ['Угол', s.angle],
    ['Хук', s.hook],
    ['Подача', s.delivery ? (DELIVERY[String(s.delivery)] ?? s.delivery) : null],
    ['Голос', voiceGender(typeof s.voice === 'string' ? s.voice : null, s.voice_gender)],
    ['Первый кадр', s.opener],
    ['Язык', s.lang ? String(s.lang).toUpperCase() : d.language?.toUpperCase()],
    ['Музыка', s.music],
    [
      'Футаж / генерации',
      typeof s.footage_share === 'number'
        ? `${Math.round(s.footage_share * 100)}% / ${Math.round(Number(s.gen_share ?? 0) * 100)}%`
        : null,
    ],
  ];
  return (
    <>
      <dl className="facts">
        {rows
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{String(v)}</dd>
            </div>
          ))}
      </dl>
      {typeof s.voice === 'string' && s.voice && (
        <details className="sp-raw">
          <summary>Технические детали</summary>
          <p className="muted">Голос озвучки: {s.voice}</p>
        </details>
      )}
    </>
  );
}

export function CreativePage() {
  const { id } = useParams({ from: '/creatives/$id' });
  const q = useCreative(Number(id));
  const qc = useQueryClient();
  const navigate = useNavigate();
  const d = q.data;
  const [versionId, setVersionId] = useState<number | null>(null);
  const [time, setTime] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'edits' | 'versions' | 'check' | 'about'>('edits');
  const [active, setActive] = useState<number | null>(null);
  const [variants, setVariants] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const v = useMemo(
    () => d?.versions.find((x) => x.id === (versionId ?? d.currentVersionId)) ?? d?.versions[0],
    [d, versionId],
  );
  const open = d?.comments.filter((c) => c.status === 'open') ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['creative', Number(id)] });

  const seek = (t: number) => {
    if (video.current) video.current.currentTime = t;
    setTime(t);
    const near = d?.comments.find((c) => Math.abs(c.tStart - t) < 0.3);
    setActive(near?.id ?? null);
  };

  const addComment = async (e: FormEvent) => {
    e.preventDefault();
    if (!v || note.trim().length < 2) return;
    setBusy(true);
    try {
      await api(`/api/creatives/${id}/comments`, {
        versionId: v.id,
        tStart: Math.round(time * 10) / 10,
        text: note.trim(),
      });
      setNote('');
      await refresh();
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: string, msg: string) => {
    try {
      await api(`/api/creatives/${id}`, { status }, 'PATCH');
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['creatives'] })]);
      toast(msg);
    } catch (err) {
      toast((err as Error).message);
    }
  };

  const revise = async () => {
    setBusy(true);
    try {
      const r = await api<{ threadId: string; comments: number }>(`/api/creatives/${id}/revise`, {});
      toast(`Правки (${r.comments}) у агента — новая версия появится здесь`);
      await refresh();
      void navigate({ to: '/agent/chats/$threadId', params: { threadId: r.threadId } });
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (q.isPending)
    return (
      <div className="pc creo-page">
        <Skeleton className="player-skel" h={0} />
        <div className="creo-side">
          <Skeleton w="60%" h={26} />
          <Skeleton h={120} />
        </div>
      </div>
    );
  if (q.isError || !d)
    return (
      <div className="pc">
        <div className="card">
          <EmptyState
            icon={<Clapperboard />}
            title="Ролик не найден"
            action={
              <Link to="/creatives" className="btn">
                К пулу креативов
              </Link>
            }
          >
            {(q.error as Error | null)?.message}
          </EmptyState>
        </div>
      </div>
    );

  const st = statusOf(d.status);
  const name = `${d.offer.slug}_${d.code}`;
  const qcSt = v ? (QC[v.qcStatus] ?? null) : null;
  // Варианты — от победителя: отобранного владельцем или уже в рекламе. Главной кнопкой — когда на экране нет
  // другого главного действия (отправить правки агенту)
  const canVariants = !!v && ['approved', 'uploaded', 'live', 'fatigued'].includes(d.status);
  const variantsMain = open.length === 0;
  return (
    <>
      <header className="ph creo-ph">
        <div>
          <Link to="/creatives" className="back-link">
            <ArrowLeft aria-hidden /> Креативы
          </Link>
          <h1>
            {d.offer.name} · <span className="mono">{d.code}</span>
          </h1>
          <p>
            <Chip tone={st.tone} dot>
              {st.label}
            </Chip>{' '}
            {d.fatigue && (
              <>
                <Chip tone="warning" dot>
                  Выгорает
                </Chip>{' '}
              </>
            )}
            {d.batch ? `батч «${d.batch.title}» · ` : ''}
            {d.costUsd > 0
              ? `генерации ${usd(d.costUsd)}`
              : d.source === 'creatives_room'
                ? 'из Creatives Room'
                : ''}
          </p>
        </div>
        <div className="ph-actions">
          {v && (
            <a className="btn" href={mediaUrl(v.video, `${name}.mp4`) ?? '#'} download>
              <Download aria-hidden />
              Скачать
            </a>
          )}
          {d.status === 'approved' ? (
            <Button onClick={() => setStatus('ready', 'Вернул в готовые')}>Вернуть в готовые</Button>
          ) : (
            ['ready', 'qc', 'fatigued', 'draft'].includes(d.status) && (
              <>
                <Button variant="danger-soft" onClick={() => setStatus('rejected', 'Ролик отклонён')}>
                  Отклонить
                </Button>
                <Button
                  variant="primary"
                  icon={<Check />}
                  onClick={() => setStatus('approved', 'Ролик отобран в рекламу')}
                >
                  В рекламу
                </Button>
              </>
            )
          )}
          {(d.status === 'rejected' || d.status === 'archived') && (
            <Button onClick={() => setStatus('ready', 'Вернул в готовые')}>Вернуть в пул</Button>
          )}
          {canVariants && (
            <Button
              variant={variantsMain ? 'primary' : 'default'}
              icon={<Shuffle />}
              onClick={() => setVariants(true)}
            >
              Сделать варианты
            </Button>
          )}
        </div>
      </header>
      <div className="pc creo-page">
        <div className="creo-stage">
          {v ? (
            <>
              <div className="player">
                {/* biome-ignore lint/a11y/useMediaCaption: субтитры вшиты в сам ролик (караоке) */}
                <video
                  key={v.id}
                  ref={video}
                  src={mediaUrl(v.video) ?? undefined}
                  poster={mediaUrl(v.poster) ?? undefined}
                  controls
                  playsInline
                  preload="none"
                  onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                  onSeeked={(e) => setTime(e.currentTarget.currentTime)}
                />
              </div>
              <Timeline
                v={v}
                time={time}
                comments={d.comments.filter((c) => c.status !== 'wontfix')}
                onSeek={seek}
                active={active}
              />
              <form className="note-form" onSubmit={addComment}>
                <span className="note-at mono">{tc(time)}</span>
                <input
                  ref={input}
                  className="input"
                  placeholder="Что поменять в этом месте: «здесь замени футаж», «цену на 199 000»"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onFocus={() => video.current?.pause()}
                  maxLength={1000}
                />
                <IconButton label="Добавить правку" type="submit" disabled={busy || note.trim().length < 2}>
                  <MessageSquarePlus />
                </IconButton>
              </form>
            </>
          ) : (
            <div className="card">
              <EmptyState icon={<Clapperboard />} title="Файла ролика ещё нет">
                Ролик из Creatives Room без файла в хранилище — перенос файлов идёт отдельно.
              </EmptyState>
            </div>
          )}
        </div>

        <aside className="creo-side">
          {d.fatigue && (
            <div className="fatigue-card">
              <p>
                <b>Ролик выгорает.</b> {d.fatigue.summary}.
              </p>
              <p className="muted">
                Агент уже знает и предложит, что делать. Можно не ждать и сделать варианты — они освежат
                ролик, не трогая товар и оффер.
              </p>
            </div>
          )}
          {open.length > 0 && (
            <div className="revise-card">
              <p>
                <b>
                  {open.length} {open.length === 1 ? 'правка ждёт' : 'правки ждут'} отправки агенту.
                </b>{' '}
                Он поменяет только эти места и сделает новую версию.
              </p>
              <Button variant="primary" icon={<Send />} onClick={revise} disabled={busy} block>
                Отправить правки агенту
              </Button>
            </div>
          )}
          <nav className="tabs tabs-sm" aria-label="Разделы ролика">
            {(
              [
                ['edits', `Правки${d.comments.length ? ` · ${d.comments.length}` : ''}`],
                ['versions', `Версии · ${d.versions.length}`],
                ['check', 'Проверка'],
                ['about', 'Сведения'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                aria-current={tab === k ? 'page' : undefined}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </nav>

          {tab === 'edits' &&
            (d.comments.length ? (
              <ul className="edits">
                {d.comments.map((c) => {
                  const cs = COMMENT_ST[c.status];
                  const ver = d.versions.find((x) => x.id === c.versionId)?.version;
                  return (
                    <li key={c.id} className={active === c.id ? 'act' : ''}>
                      <button type="button" className="edit-time mono" onClick={() => seek(c.tStart)}>
                        {tc(c.tStart)}
                      </button>
                      <div className="edit-b">
                        <p>{c.text}</p>
                        <span className="muted">
                          <Chip tone={cs.tone}>{cs.label}</Chip> v{ver} · {when(c.createdAt)}
                        </span>
                        {c.reply && <p className="edit-reply">Агент: {c.reply}</p>}
                      </div>
                      {c.status === 'open' && (
                        <IconButton
                          label="Удалить правку"
                          onClick={async () => {
                            try {
                              await api(`/api/creatives/comments/${c.id}`, undefined, 'DELETE');
                              await refresh();
                            } catch (err) {
                              toast((err as Error).message);
                            }
                          }}
                        >
                          <Trash2 />
                        </IconButton>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted edits-empty">
                Остановите ролик в нужном месте и напишите под плеером, что поменять. Агент получит правки с
                таймкодами.
              </p>
            ))}

          {tab === 'versions' && (
            <ul className="versions">
              {d.versions.map((x) => {
                const q2 = QC[x.qcStatus];
                return (
                  <li key={x.id}>
                    <button
                      type="button"
                      className={`ver${x.id === v?.id ? ' on' : ''}`}
                      onClick={() => {
                        setVersionId(x.id);
                        setTime(0);
                      }}
                      aria-pressed={x.id === v?.id}
                    >
                      <span className="ver-n mono">v{x.version}</span>
                      <span className="ver-b">
                        <b>{x.changeSummary ?? (x.version === 1 ? 'Первая версия' : 'Новая версия')}</b>
                        {x.diff?.items.length ? <span className="ver-diff">{x.diff.summary}</span> : null}
                        <span className="muted">
                          {when(x.createdAt)}
                          {x.costUsd > 0 ? ` · ${usd(x.costUsd)}` : ''}
                          {q2 ? ` · ${q2.label.toLowerCase()}` : ''}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {tab === 'check' && v && (
            <div className="check-tab">
              {qcSt && (
                <p>
                  <Chip tone={qcSt.tone} dot>
                    {qcSt.label}
                  </Chip>{' '}
                  <span className="muted">v{v.version}</span>
                </p>
              )}
              <QcList v={v} />
              {v.judge && (
                <div className="judge">
                  <b>Оценка ролика ИИ: {v.judge.score} из 10</b>
                  {v.judge.issues.length > 0 && (
                    <ul>
                      {v.judge.issues.map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                  )}
                  {v.judge.notes && <p className="muted">{v.judge.notes}</p>}
                </div>
              )}
              {v.sheet && (
                <a href={mediaUrl(v.sheet) ?? '#'} target="_blank" rel="noreferrer" className="sheet-link">
                  <img src={mediaUrl(v.sheet) ?? ''} alt="Лист кадров: кадр в секунду" loading="lazy" />
                </a>
              )}
            </div>
          )}

          {tab === 'about' && (
            <div className="about-tab">
              <Spec d={d} />
              {d.script && (
                <section>
                  <h3>Текст диктора</h3>
                  <p className="script">{d.script}</p>
                  {d.scriptRu && <p className="muted script">{d.scriptRu}</p>}
                </section>
              )}
              {(d.adHeadline || d.adText) && (
                <section>
                  <h3>Объявление</h3>
                  {d.adHeadline && (
                    <div className="copy-row">
                      <b>{d.adHeadline}</b>
                      <IconButton label="Скопировать заголовок" onClick={() => copy(d.adHeadline ?? '')}>
                        <Copy />
                      </IconButton>
                    </div>
                  )}
                  {d.adText && (
                    <div className="copy-row">
                      <span>{d.adText}</span>
                      <IconButton label="Скопировать текст" onClick={() => copy(d.adText ?? '')}>
                        <Copy />
                      </IconButton>
                    </div>
                  )}
                </section>
              )}
              {d.usages.length > 0 && (
                <section>
                  <h3>Из чего собран</h3>
                  <ul className="usages">
                    {d.usages.map((u) => (
                      <li key={`${u.role}${u.path}`}>
                        <span className="muted">
                          {u.role === 'footage'
                            ? 'футаж'
                            : u.role === 'gen'
                              ? 'генерация'
                              : u.role === 'voice'
                                ? 'озвучка'
                                : u.role === 'music'
                                  ? 'музыка'
                                  : 'картинка'}
                        </span>
                        <span>{u.title}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </aside>
      </div>
      {canVariants && (
        <VariantsPanel
          open={variants}
          onClose={() => setVariants(false)}
          creative={{ id: d.id, code: d.code, fatigue: d.fatigue ?? null }}
        />
      )}
    </>
  );
}
